// @writing-suite/editor: the scrivenings editor (W4), ported from SuiteEditor. The app imports `createScrivenings`
// and `commands`, and `@writing-suite/editor/style.css` once.
import * as cmd from './commands.ts';
import { setScreenplayElement, setScreenplaySong, screenplayTab } from './screenplay.ts';

export {
  createScrivenings, editsOf, minimalChange, scriveningsExtensions, scriveningsKeymap,
  type ScriveningsCreateOptions, type ScriveningsHandle, type Theme,
} from './editor.ts';
export type { LinkAtSelection, ProseFormat } from './commands.ts';
export { linkAtSelection } from './commands.ts';
export { linkAtPosition, openLinkOnModClick } from './editor.ts';
export type { BlockStyle } from './blocks.ts';
export { elementName, STAGE_ELEMENTS, WRITABLE_ELEMENTS, writableElements } from './screenplay.ts';
export { isPlayLikeFormat } from './state.ts';
export { MARKDOWN_MIME, copied, pasteSpec } from './clipboard.ts';
export { metrics } from './filter.ts';
export { stylerStats } from './decorations.ts';
/** Transaction annotations: `Exact` edits aren't balanced again (the app's own replacements), `External` ones aren't
 *  reported or undoable (the documents' changes). */
export { Exact, External, isLocked, lockedField, setLockedFragments } from './state.ts';
export {
  annotationField, annotationRange, annotations, annotationsAt, setActiveAnnotation, setAnnotations, type Annotation,
} from './annotations.ts';

/** The format bar's and the keys' commands. Each is a CodeMirror `StateCommand`: `editor.run(commands.toggleItalic)`. */
export const commands = {
  // Inline
  toggleItalic: cmd.toggleItalic,
  toggleBold: cmd.toggleBold,
  toggleUnderline: cmd.toggleUnderline,
  toggleSmallCaps: cmd.toggleSmallCaps,
  setHighlight: cmd.setHighlight,
  setTextColor: cmd.setTextColor,
  setLink: cmd.setLink,
  removeLink: cmd.removeLink,
  // Blocks
  setAlignment: cmd.setAlignment,
  setList: cmd.setList,
  setBlockStyle: cmd.setBlockStyle,
  insertSceneBreak: cmd.insertSceneBreak,
  insertFootnote: cmd.insertFootnote,
  renumberList: cmd.renumberList,
  // Keys
  insertNewline: cmd.insertNewline,
  insertLineBreak: cmd.insertLineBreak,
  deleteBackward: cmd.deleteBackward,
  deleteForward: cmd.deleteForward,
  continueList: cmd.continueList,
  removeListMarker: cmd.removeListMarker,
  insertTab: cmd.insertTab,
  // Verse
  continueVerse: cmd.continueVerse,
  verseTab: cmd.verseTab,
  // Screenplays
  setScreenplayElement,
  /** A musical's song (⌘7). */
  setScreenplaySong,
  screenplayTab,
} as const;

export {
  AUTHOR_HUES, REVISION_COLOURS, revisionClass, revisionField, revisionMarks, revisionMarksByFragment, revisions, setRevisionMarks, shiftRevisionMarks,
  type RevisionChangeLike, type RevisionDeletion, type RevisionInsertion, type RevisionMarkState, type RevisionMarksSpec,
} from './revisions.ts';
export { textEdits, visibleEdits, type TextEdit } from './textEdits.ts';

// The rules, for tests and tools.
export * as markdown from './markdown.ts';
export * as rules from './rules.ts';
export * as blocks from './blocks.ts';

// Live co-writing (W9b): other writers' carets, and markers balanced after a merge.
export {
  fragmentBody, localSelection, remoteCaretField, remoteCaretPositions, remoteCarets, setRemoteCarets, shownFragmentIds, type RemoteCaret,
  type RemoteCaretsSpec,
} from './presence.ts';
export {
  changedRange, isBalanced, markupInParents, mergeFix, rebalanceDeletions, rebalanceSpec, touchesMarkup, type CursorBackend,
  type Deletion,
} from './rebalance.ts';
